import type { SettingsEvent } from "@/features/settings/mediators/settingsEvents";
import {
  defaultMixBoardSetting,
  type MixBoardSettings,
} from "@/features/settings/stores/mixStore";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type BoardSelectionEvent = Extract<
  SettingsEvent,
  {
    type:
      | "settings/board-selection-synced"
      | "settings/board-selection-toggled"
      | "settings/board-selection-all-toggled"
      | "settings/board-help-toggled"
      | "settings/board-help-dismissed";
  }
>;

export interface BoardSelectionState {
  readonly boardSettings: MixBoardSettings;
  readonly registeredSlugs: readonly string[];
  readonly minBoardWarning: boolean;
  readonly openHelp: string | null;
  readonly alignHelpRight: boolean;
}

export interface BoardSelectionEffect {
  readonly type: "set-board-enabled";
  readonly slug: string;
  readonly enabled: boolean;
}

export function isBoardEnabled(
  boardSettings: MixBoardSettings,
  slug: string,
): boolean {
  return (boardSettings[slug] ?? defaultMixBoardSetting(slug)).enabled;
}

export function createBoardSelectionMediator(
  boardSettings: MixBoardSettings,
  registeredSlugs: readonly string[],
  runEffect: (effect: BoardSelectionEffect) => void,
): Mediator<BoardSelectionState, BoardSelectionEvent, BoardSelectionEffect> {
  return createMediator<
    BoardSelectionState,
    BoardSelectionEvent,
    BoardSelectionEffect
  >(
    {
      boardSettings,
      registeredSlugs,
      minBoardWarning: false,
      openHelp: null,
      alignHelpRight: false,
    },
    (state, event) => {
      switch (event.type) {
        case "settings/board-selection-synced":
          return {
            status: "handled",
            state: {
              ...state,
              boardSettings: event.boardSettings,
              registeredSlugs: event.registeredSlugs,
            },
          };
        case "settings/board-selection-toggled": {
          const enabledCount = state.registeredSlugs.filter((slug) =>
            isBoardEnabled(state.boardSettings, slug),
          ).length;
          if (!event.enabled && enabledCount <= 1) {
            return {
              status: "handled",
              state: { ...state, minBoardWarning: true },
            };
          }
          return {
            status: "handled",
            state: { ...state, minBoardWarning: false },
            effects: [
              {
                type: "set-board-enabled",
                slug: event.slug,
                enabled: event.enabled,
              },
            ],
          };
        }
        case "settings/board-selection-all-toggled": {
          const allSelected = state.registeredSlugs.every((slug) =>
            isBoardEnabled(state.boardSettings, slug),
          );
          const remainingSlug = state.registeredSlugs.includes("aimg")
            ? "aimg"
            : state.registeredSlugs[0];
          const effects = state.registeredSlugs.flatMap((slug) => {
            const enabled = !allSelected || slug === remainingSlug;
            return isBoardEnabled(state.boardSettings, slug) === enabled
              ? []
              : [{ type: "set-board-enabled" as const, slug, enabled }];
          });
          return {
            status: "handled",
            state: { ...state, minBoardWarning: false },
            effects,
          };
        }
        case "settings/board-help-toggled":
          return {
            status: "handled",
            state: {
              ...state,
              openHelp: state.openHelp === event.slug ? null : event.slug,
              alignHelpRight: event.alignRight,
            },
          };
        case "settings/board-help-dismissed":
          return {
            status: "handled",
            state: { ...state, openHelp: null },
          };
      }
    },
    { runEffect },
  );
}
